import { ICON_NAMES } from '../icon/icon.helper';
import { useStore } from 'src/contexts/StoreContext';

import { DYNAMIC_MODAL_VIEWS } from 'src/store/_models/dynamic-modal-layer/dynamicModalLayer.constants';
import amplitude from 'src/_shared/amplitude';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';
import { observer } from 'mobx-react-lite';
import { ProductModelInstance } from 'src/store/_models/product';
import { styles } from './PreviewUtilsBar.styles';
import { TestIDs } from 'src/constants/testIDs.constants';
import { productTestID } from 'src/helpers/testIDs.helper';
import { CircularButton } from '../buttons/circular-button/CircularButton';
import { FocusGroups } from '../focus-provider/FocusProvider.constants';
import { CIRCULAR_BUTTON_SIZES, CIRCULAR_BUTTON_TYPES } from '../buttons/circular-button/CircularButton.constants';
import { useResponsiveScale } from '../../contexts/ResponsiveScaleContext';
import Animated, { useAnimatedStyle } from 'react-native-reanimated';
import { BUTTON_SIZES } from 'src/editor-v3/components/buttons/button/Button.constants';
import { QuaternaryActionButton } from 'src/editor-v3/components/buttons/quaternary-action-button/QuaternaryActionButton';
import { ACTION_BUTTON_MODE } from 'src/editor-v3/components/buttons/quaternary-action-button/QuaternaryActionButton.types';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';
import { BaseHoverTooltip } from '../tooltips/base-hover-tooltip/BaseHoverTooltip';
import { TooltipPosition } from '../tooltips/Tooltip.types';
import TextLato from '../text/text-lato/TextLato';

const PreviewUtilsBar = observer(({ index, product }: { index?: number; product: ProductModelInstance }) => {
  const {
    i18n,
    project: {
      isProductEditModeActive,
      activeProductIndex,
      totalNumberOfProducts,
      goToMultiphotoEditorPage,
      selectUserImage,
      isProjectAddedToCart,
      isProductSetProject,
    },
    dynamicModal: { openModal },
  } = useStore();

  const { isDesktop } = useResponsiveScreen();
  const { responsiveScaleSV, responsiveScale } = useResponsiveScale();
  const animatedStyle = useAnimatedStyle(
    () => ({
      width: product.preview.viewBox.width * responsiveScaleSV.value,
      top: -36,
      right: 0,
    }),
    [responsiveScaleSV, product.preview.viewBox.width, responsiveScale],
  );

  // check if the product is multiphoto and all userImageAreas are filled
  const isMultiphotoProductComplete =
    product.preview.isMultiphoto &&
    !isProductEditModeActive &&
    product.preview.userImageAreas.filter(e => e.userImage === undefined).length === 0;

  const isActiveIndex = index === activeProductIndex;

  const onPressDelete = async () => {
    openModal({ dynamicModalView: DYNAMIC_MODAL_VIEWS.DeleteProduct });
    amplitude.trackEvent(AnalyticsEvents.personalize.deleteProductClick, { n_products: totalNumberOfProducts });
  };

  const onPressEdit = async (isMultiphoto: boolean) => {
    // for multiphoto product, there's another layer of editing
    if (isMultiphoto) {
      goToMultiphotoEditorPage();
    } else {
      const userImage = product.userImages[0];

      await selectUserImage(userImage);
    }
  };

  const onReplaceProductSetPressHandler = async () => {
    openModal({
      dynamicModalView: DYNAMIC_MODAL_VIEWS.UploadMultiSource,
      isReplacingPhoto: true,
      uploadMultiSourceCallback: params => {
        if (!!params?.files) {
          product.preview.userImageArea.userImage?.replaceImage({ files: params.files });
        }
      },
    });
    amplitude.trackEvent(AnalyticsEvents.personalize.addPhotoClick);
  };

  let showEditIcon = false;

  if (product.preview.isMultiphoto) {
    showEditIcon = isMultiphotoProductComplete;
  } else {
    if (isProductSetProject) {
      showEditIcon = product.preview.userImageArea.hasUserImage;
    } else {
      showEditIcon = true;
    }
  }

  return (
    <Animated.View
      testID={TestIDs.PREVIEW_BAR}
      pointerEvents={!isActiveIndex ? 'none' : 'auto'}
      style={[
        styles.buttonsWrapper,
        animatedStyle,
        !isActiveIndex && { opacity: 0 },
        !isDesktop && { justifyContent: 'center' },
      ]}
    >
      {isProductSetProject && product.preview.userImageArea.hasUserImage && (
        <QuaternaryActionButton
          mode={ACTION_BUTTON_MODE.LABEL}
          size={BUTTON_SIZES.SMALL}
          text={i18n.t('edit:replace')}
          iconName={ICON_NAMES.Refresh}
          onPress={onReplaceProductSetPressHandler}
          focusId={productTestID(product.id, TestIDs.REPLACE)}
          focusOrder={0}
          focusGroup={FocusGroups.BEFORE_PRODUCT}
        />
      )}
      {showEditIcon && (
        <BaseHoverTooltip
          position={TooltipPosition.TOP}
          containerStyle={styles.hoveredTooltipContentIconContainer}
          TooltipContent={() => (
            <TextLato style={styles.hoveredTooltipContentIcon}>
              {i18n.t('actions:edit_x', { x: i18n.t('general:photo') })}
            </TextLato>
          )}
        >
          <CircularButton
            accessible={true}
            accessibilityLabel={i18n.t('actions:edit_x', { x: i18n.t('general:photo') })}
            accessibilityRole="button"
            onPress={() => onPressEdit(isMultiphotoProductComplete)}
            testID={productTestID(product.id, TestIDs.EDIT)}
            iconName={ICON_NAMES.Edit}
            iconStyle={styles.icon}
            focusId={productTestID(product.id, TestIDs.EDIT)}
            focusOrder={1}
            focusGroup={FocusGroups.BEFORE_PRODUCT}
            focusable={isActiveIndex}
            size={CIRCULAR_BUTTON_SIZES.LARGE}
            type={CIRCULAR_BUTTON_TYPES.FILL}
          />
        </BaseHoverTooltip>
      )}

      {!isProjectAddedToCart && !isProductEditModeActive && !isProductSetProject && (
        <BaseHoverTooltip
          position={TooltipPosition.TOP}
          containerStyle={styles.hoveredTooltipContentIconContainer}
          TooltipContent={() => (
            <TextLato style={styles.hoveredTooltipContentIcon}>{i18n.t('actions:delete')}</TextLato>
          )}
        >
          <CircularButton
            accessible={true}
            accessibilityLabel={i18n.t('actions:delete')}
            accessibilityRole="button"
            onPress={onPressDelete}
            testID={productTestID(product.id, TestIDs.DELETE)}
            iconName={ICON_NAMES.Delete}
            iconStyle={styles.icon}
            focusId={productTestID(product.id, TestIDs.DELETE)}
            focusOrder={2}
            focusGroup={FocusGroups.BEFORE_PRODUCT}
            focusable={isActiveIndex}
            size={CIRCULAR_BUTTON_SIZES.LARGE}
            type={CIRCULAR_BUTTON_TYPES.FILL}
          />
        </BaseHoverTooltip>
      )}
    </Animated.View>
  );
});

PreviewUtilsBar.displayName = 'PreviewUtilsBar';

export default PreviewUtilsBar;
